import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Controller } from "react-hook-form";

import { Button } from "../../../components";
import { encodeBase64String } from "../../../utils/base64";
import {
  AiInterviewEmailType,
  AiInterviewerFragment,
  useSendTestAiInterviewEmailMutation,
} from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import EmailBodyEditor from "./EmailBodyEditor/EmailBodyEditor";
import NotificationDisabledAlert from "./NotificationDisabledAlert";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

interface AiInterviewerNotificationsReminderSettingsProps {
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
}

const AiInterviewerNotificationsReminderSettings: React.FC<
  AiInterviewerNotificationsReminderSettingsProps
> = ({ aiInterviewer, readOnly }) => {
  const { register, watch, control } = useAiInterviewerFormContext();
  const currentUser = useCurrentUser();
  const toast = useToast();

  // Get the reminder email config (always present with defaults if not customized)
  const config = aiInterviewer.currentVersion.reminderEmailConfig;

  // Available wildcards for reminder emails
  const availableWildcards = [
    { name: "candidate_name", label: "Candidate's name" },
    { name: "position_name", label: "Position title" },
    { name: "days_since_invite_sent", label: "Days since invite sent" },
    { name: "invite_expiration_days", label: "Link expiry (days)" },
  ];

  // Watch form values for validation
  const reminderEmailEnabled = watch("reminderEmailEnabled");
  const reminderDays = watch("reminderDays");
  const inviteExpirationDays = watch("inviteExpirationDays");

  // The form is disabled if the notification is turned off OR we're read-only.
  // The two states are visually identical except the disabled state shows an
  // info alert at the top of the form.
  const isDisabled = readOnly || !reminderEmailEnabled;

  // Validation: reminder must be before expiration when enabled
  const hasExpirationError =
    reminderEmailEnabled &&
    reminderDays &&
    inviteExpirationDays &&
    reminderDays >= inviteExpirationDays;

  // Mutation for sending test email
  const [sendTestEmail, { loading: sendingTestEmail }] =
    useSendTestAiInterviewEmailMutation({
      onCompleted: (data) => {
        toast({
          title: "Test email sent",
          description: `Test reminder sent to ${
            data.sendTestAiInterviewEmail?.recipientEmail ?? currentUser.email
          }`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      },
      onError: (error) => {
        toast({
          title: "Failed to send test email",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      },
    });

  const handleSendTestEmail = () => {
    // Get current form values for the reminder email config
    const reminderData = watch("emailConfig.reminder");

    // Encode the body HTML to base64 for transmission
    const reminderWithEncodedHtml = {
      ...reminderData,
      bodyHtml: encodeBase64String(reminderData.bodyHtml) ?? "",
    };

    sendTestEmail({
      variables: {
        aiInterviewerId: aiInterviewer.id,
        emailType: AiInterviewEmailType.Reminder,
        emailConfig: JSON.stringify(reminderWithEncodedHtml),
      },
    });
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      <HStack justifyContent="space-between" alignItems="center">
        <VStack spacing={1}>
          <AiInterviewerSectionTitle mb={0} title="Reminder email" />
          <Flex flexDirection="column">
            <Text color="gray.600" fontSize="sm">
              Customize the email sent to candidates reminding them to take an
              AI-moderated interview.
            </Text>
          </Flex>
        </VStack>
        <Button
          onClick={handleSendTestEmail}
          isLoading={sendingTestEmail}
          isDisabled={!reminderEmailEnabled}
          colorScheme="blue"
          variant="outline"
          size="sm"
        >
          Send Test Email
        </Button>
      </HStack>
      {!reminderEmailEnabled && (
        <NotificationDisabledAlert notificationName="Reminder email" />
      )}
      <FormControl id="reminderDays" isInvalid={!!hasExpirationError}>
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Send timing
        </FormLabel>
        <HStack>
          <Select
            {...register("reminderDays")}
            defaultValue={aiInterviewer.currentVersion.reminderDays}
            fontSize="sm"
            borderRadius="md"
            width="auto"
            minW="60px"
            isDisabled={isDisabled}
            _disabled={{
              bg: "gray.50",
            }}
          >
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={6}>6</option>
            <option value={7}>7</option>
            <option value={8}>8</option>
            <option value={9}>9</option>
            <option value={10}>10</option>
          </Select>
          <FormHelperText fontSize="sm" color="gray.600">
            days after invite if the candidate has not completed the interview
          </FormHelperText>
        </HStack>
        {hasExpirationError && (
          <FormErrorMessage>
            Reminder emails must be sent before the invitation expires
            (currently {inviteExpirationDays} days)
          </FormErrorMessage>
        )}
      </FormControl>
      <FormControl id="fromEmailName">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From email name
        </FormLabel>
        <Input
          {...register("emailConfig.reminder.fromName")}
          defaultValue={config.fromName}
          fontSize="sm"
          borderRadius="md"
          isDisabled={isDisabled}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="fromEmailAddress">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From email address
        </FormLabel>
        <Input
          {...register("emailConfig.reminder.fromEmail")}
          defaultValue={config.fromEmail}
          fontSize="sm"
          borderRadius="md"
          isDisabled
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="replyToEmailAddress">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Reply-to email address
        </FormLabel>
        <Input
          {...register("emailConfig.reminder.replyTo")}
          defaultValue={config.replyTo}
          fontSize="sm"
          borderRadius="md"
          isDisabled={isDisabled}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="emailSubject">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Email subject
        </FormLabel>
        <Input
          {...register("emailConfig.reminder.subject")}
          defaultValue={config.subject}
          fontSize="sm"
          borderRadius="md"
          isDisabled={isDisabled}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="emailBody">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Email body
        </FormLabel>
        <Controller
          name="emailConfig.reminder.bodyHtml"
          control={control}
          defaultValue={config.bodyHtml}
          render={({ field }) => (
            <EmailBodyEditor
              value={field.value}
              fieldName={field.name}
              onChange={field.onChange}
              readOnly={isDisabled}
              placeholder="Enter email body..."
              availableWildcards={availableWildcards}
            />
          )}
        />
      </FormControl>
    </VStack>
  );
};

export { AiInterviewerNotificationsReminderSettings };
